import { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Loader2, LockKeyhole, LogOut, QrCode, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext.jsx';
import { useTranslation } from '@/components/i18n/TranslationProvider.jsx';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { getUserRole } from '@/utils/auth.js';
import { supabase } from '@/utils/supabaseClient.js';
import {
  getMfaFactorType,
  isLegacyManualMfaFactor,
  isVerifiedMfaFactor,
  isVerifiedNonLegacyTotpFactor,
  toMfaFactorArray,
} from '../../../shared/mfaFactors.js';

const ADMIN_TOTP_FRIENDLY_NAME = 'GameplaySync Admin';
const TOTP_CODE_PATTERN = /^\d{6}$/u;

const findVerifiedTotp = (factors) => factors.find(isVerifiedNonLegacyTotpFactor) || null;

const translateGateError = (t, errorKey) => {
  switch (errorKey) {
    case 'auth.admin_mfa_cleanup_error':
      return t('auth.admin_mfa_cleanup_error');
    case 'auth.admin_mfa_code_invalid':
      return t('auth.admin_mfa_code_invalid');
    case 'auth.admin_mfa_enrollment_error':
      return t('auth.admin_mfa_enrollment_error');
    case 'auth.admin_mfa_session_unavailable':
      return t('auth.admin_mfa_session_unavailable');
    case 'auth.admin_mfa_sign_out_error':
      return t('auth.admin_mfa_sign_out_error');
    default:
      return t('auth.admin_mfa_unavailable');
  }
};

const listFactors = async () => {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error || !data) {
    throw error || new Error('MFA factors unavailable.');
  }
  return toMfaFactorArray(data);
};

const assertAal2 = async () => {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || data?.currentLevel !== 'aal2') {
    throw error || new Error('AAL2 session unavailable.');
  }
};

const removeLegacyFactors = async (factors) => {
  const legacyFactors = factors.filter(
    (factor) => factor?.id && isLegacyManualMfaFactor(factor),
  );

  for (const factor of legacyFactors) {
    const { error } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
    if (error) {
      throw error;
    }
  }

  if (legacyFactors.length === 0) {
    return factors;
  }

  const readbackFactors = await listFactors();
  if (readbackFactors.some(isLegacyManualMfaFactor)) {
    throw new Error('Legacy MFA factor cleanup was not confirmed.');
  }
  return readbackFactors;
};

const GateShell = ({ children }) => (
  <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-8">
    <Card className="w-full max-w-lg border-primary/30 bg-slate-950/95 text-slate-100 shadow-2xl">
      {children}
    </Card>
  </div>
);

const AdminMfaBoundary = ({ children }) => {
  const { t } = useTranslation();
  const { session, user, signOut } = useAuth();
  const [status, setStatus] = useState('checking');
  const [factorId, setFactorId] = useState('');
  const [knownFactors, setKnownFactors] = useState([]);
  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [errorKey, setErrorKey] = useState('');
  const [checkVersion, setCheckVersion] = useState(0);
  const role = getUserRole(user || session?.user);
  const hasRuntimeSession = Boolean(session?.access_token);

  const prepareGate = useCallback(async () => {
    setStatus('checking');
    setErrorKey('');
    setCode('');

    const { data: aal, error: aalError } =
      await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aalError || !aal?.currentLevel) {
      throw aalError || new Error('MFA assurance level unavailable.');
    }

    const factors = await listFactors();
    setKnownFactors(factors);
    const verifiedTotp = findVerifiedTotp(factors);

    if (!verifiedTotp) {
      setFactorId('');
      setStatus('enrollment_required');
      return;
    }

    if (aal.currentLevel !== 'aal2') {
      setFactorId(verifiedTotp.id);
      setStatus('challenge_required');
      return;
    }

    const cleanedFactors = await removeLegacyFactors(factors);
    if (!findVerifiedTotp(cleanedFactors)) {
      throw new Error('Verified administrator TOTP factor unavailable.');
    }
    setStatus('ready');
  }, []);

  useEffect(() => {
    let isActive = true;

    if (role !== 'admin') {
      setStatus('ready');
      return () => {
        isActive = false;
      };
    }

    if (!hasRuntimeSession && import.meta.env.DEV) {
      setStatus('ready');
      return () => {
        isActive = false;
      };
    }

    if (!hasRuntimeSession) {
      setStatus('unavailable');
      setErrorKey('auth.admin_mfa_session_unavailable');
      return () => {
        isActive = false;
      };
    }

    prepareGate().catch(() => {
      if (!isActive) {
        return;
      }
      setStatus('unavailable');
      setErrorKey('auth.admin_mfa_unavailable');
    });

    return () => {
      isActive = false;
    };
  }, [checkVersion, hasRuntimeSession, prepareGate, role, user?.id]);

  const handleStartEnrollment = async () => {
    if (status === 'enrolling') {
      return;
    }

    setStatus('enrolling');
    setErrorKey('');
    setCode('');
    setShowSecret(false);

    try {
      const abandonedFactors = knownFactors.filter(
        (factor) => factor?.id
          && getMfaFactorType(factor) === 'totp'
          && !isVerifiedMfaFactor(factor)
          && !isLegacyManualMfaFactor(factor),
      );
      for (const factor of abandonedFactors) {
        const { error } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
        if (error) {
          throw error;
        }
      }

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: ADMIN_TOTP_FRIENDLY_NAME,
      });
      if (error || !data?.id || !data?.totp?.qr_code || !data?.totp?.secret) {
        throw error || new Error('TOTP enrollment response incomplete.');
      }

      setEnrollment({
        factorId: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
      });
      setFactorId(data.id);
      setStatus('enrollment_ready');
    } catch {
      setStatus('unavailable');
      setErrorKey('auth.admin_mfa_enrollment_error');
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (!TOTP_CODE_PATTERN.test(code) || !factorId || status === 'verifying') {
      setErrorKey('auth.admin_mfa_code_invalid');
      return;
    }

    const wasEnrollment = status === 'enrollment_ready';
    setStatus('verifying');
    setErrorKey('');

    let verificationSucceeded = false;
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
      if (error) {
        throw error;
      }
      verificationSucceeded = true;

      await assertAal2();
      const factors = await listFactors();
      const verifiedTotp = findVerifiedTotp(factors);
      if (!verifiedTotp || (wasEnrollment && verifiedTotp.id !== factorId)) {
        throw new Error('TOTP factor verification was not confirmed.');
      }

      const cleanedFactors = await removeLegacyFactors(factors);
      if (!findVerifiedTotp(cleanedFactors)) {
        throw new Error('Administrator TOTP factor readback failed.');
      }

      setEnrollment(null);
      setCode('');
      setStatus('ready');
    } catch {
      setCode('');
      if (verificationSucceeded) {
        setStatus('unavailable');
        setErrorKey('auth.admin_mfa_cleanup_error');
      } else {
        setStatus(wasEnrollment ? 'enrollment_ready' : 'challenge_required');
        setErrorKey('auth.admin_mfa_code_invalid');
      }
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      setErrorKey('auth.admin_mfa_sign_out_error');
    }
  };

  const canSubmitCode = useMemo(() => TOTP_CODE_PATTERN.test(code), [code]);

  if (status === 'ready') {
    return children;
  }

  if (status === 'checking' || status === 'enrolling') {
    return (
      <GateShell>
        <CardContent className="flex min-h-56 flex-col items-center justify-center gap-3 p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
          <p className="text-sm text-slate-300">
            {status === 'enrolling'
              ? t('auth.admin_mfa_enrolling')
              : t('auth.admin_mfa_checking')}
          </p>
        </CardContent>
      </GateShell>
    );
  }

  if (status === 'unavailable') {
    return (
      <GateShell>
        <CardHeader className="items-center text-center">
          <LockKeyhole className="mb-2 h-9 w-9 text-amber-300" aria-hidden="true" />
          <CardTitle>{t('auth.admin_mfa_blocked_title')}</CardTitle>
          <CardDescription className="text-slate-300">
            {translateGateError(t, errorKey)}
          </CardDescription>
        </CardHeader>
        <CardFooter className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button type="button" onClick={() => setCheckVersion((value) => value + 1)}>
            {t('auth.admin_mfa_retry')}
          </Button>
          <Button type="button" variant="outline" onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('auth.admin_mfa_sign_out')}
          </Button>
        </CardFooter>
      </GateShell>
    );
  }

  if (status === 'enrollment_required') {
    return (
      <GateShell>
        <CardHeader className="items-center text-center">
          <ShieldCheck className="mb-2 h-10 w-10 text-primary" aria-hidden="true" />
          <CardTitle>{t('auth.admin_mfa_enroll_title')}</CardTitle>
          <CardDescription className="text-slate-300">
            {t('auth.admin_mfa_enroll_description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 text-sm text-amber-100">
            {t('auth.admin_mfa_legacy_notice')}
          </p>
        </CardContent>
        <CardFooter className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button type="button" onClick={handleStartEnrollment}>
            <QrCode className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('auth.admin_mfa_enroll_start')}
          </Button>
          <Button type="button" variant="outline" onClick={handleSignOut}>
            {t('auth.admin_mfa_sign_out')}
          </Button>
        </CardFooter>
      </GateShell>
    );
  }

  const isEnrollment = status === 'enrollment_ready' || Boolean(enrollment);
  const isVerifying = status === 'verifying';

  return (
    <GateShell>
      <CardHeader className="items-center text-center">
        <ShieldCheck className="mb-2 h-10 w-10 text-primary" aria-hidden="true" />
        <CardTitle>
          {isEnrollment
            ? t('auth.admin_mfa_enroll_scan_title')
            : t('auth.admin_mfa_challenge_title')}
        </CardTitle>
        <CardDescription className="text-slate-300">
          {isEnrollment
            ? t('auth.admin_mfa_enroll_scan_description')
            : t('auth.admin_mfa_challenge_description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {isEnrollment && enrollment ? (
          <div className="space-y-4">
            <div className="mx-auto w-fit rounded-xl bg-white p-3">
              <img
                src={enrollment.qrCode}
                alt={t('auth.admin_mfa_qr_alt')}
                className="h-48 w-48 max-w-full"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="admin-mfa-secret" className="text-sm font-medium text-slate-200">
                {t('auth.admin_mfa_secret_label')}
              </label>
              <div className="flex gap-2">
                <Input
                  id="admin-mfa-secret"
                  type={showSecret ? 'text' : 'password'}
                  value={enrollment.secret}
                  readOnly
                  autoComplete="off"
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setShowSecret((value) => !value)}
                  aria-label={showSecret
                    ? t('auth.admin_mfa_secret_hide')
                    : t('auth.admin_mfa_secret_show')}
                >
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        <form className="space-y-4" onSubmit={handleVerify}>
          <div className="space-y-2">
            <label htmlFor="admin-mfa-code" className="text-sm font-medium text-slate-200">
              {t('auth.admin_mfa_code_label')}
            </label>
            <Input
              id="admin-mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/gu, '').slice(0, 6))}
              disabled={isVerifying}
              required
            />
          </div>
          {errorKey ? (
            <p role="alert" className="text-sm text-red-300">
              {translateGateError(t, errorKey)}
            </p>
          ) : null}
          <Button type="submit" className="w-full" disabled={!canSubmitCode || isVerifying}>
            {isVerifying ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            {isVerifying
              ? t('auth.admin_mfa_verifying')
              : t('auth.admin_mfa_verify')}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="justify-center">
        <Button type="button" variant="ghost" onClick={handleSignOut}>
          {t('auth.admin_mfa_sign_out')}
        </Button>
      </CardFooter>
    </GateShell>
  );
};

export default AdminMfaBoundary;
